import React, { useState, useEffect, useCallback, useRef, memo } from 'react';
import { useParams } from 'react-router-dom';
import { useProject } from '@/context/ProjectContext';
import { useAuth } from '@/context/AuthContext';
import { 
  stakeholdersAPI, 
  segmentsAPI, 
  questionsAPI, 
  projectMappingCategoriesAPI, 
  aboutOthersAPI,
  mappingCategoriesLibraryAPI 
} from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Skeleton } from '@/components/ui/skeleton';
import { Slider } from '@/components/ui/slider';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import PrivacyReminder from '@/components/PrivacyReminder';
import { GalaxyIcon } from '@/components/icons';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { toast } from 'sonner';
import { 
  UsersRound, 
  Plus,
  Search,
  Users,
  UserPlus,
  List,
  LayoutGrid,
  Check,
  X,
  ChevronRight,
  Edit,
  Trash2,
  Save,
  Eye,
  Layers,
  Target,
  Link as LinkIcon,
  SkipForward,
  Ban,
  Table2,
  Filter,
  ArrowUpDown,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import ForceGraph2D from 'react-force-graph-2d';
import * as d3 from 'd3-force';

// Skip reasons for questions
const SKIP_REASONS = [
  { value: 'not_applicable', label: 'Not applicable' },
  { value: 'insufficient_info', label: "Don't have enough information" },
  { value: 'prefer_not_answer', label: 'Prefer not to answer' },
  { value: 'unsure', label: "Unsure / Don't know" },
  { value: 'other', label: 'Other reason' },
];

// Memoized Add New Stakeholder Dialog to prevent Galaxy re-renders on typing
const AddNewStakeholderDialog = memo(function AddNewStakeholderDialog({
  open,
  onClose,
  segmentTypes,
  segments,
  mappingCategories,
  onSubmit,
  saving
}) {
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    project_title: '',
    segment_assignments: [],
    identification_comment: '',
    mapping_category_ids: []
  });

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      setFormData({
        first_name: '',
        last_name: '',
        email: '',
        project_title: '',
        segment_assignments: [],
        identification_comment: '',
        mapping_category_ids: []
      });
    }
  }, [open]);

  const handleSubmit = () => {
    onSubmit(formData);
  };

  const handleClose = () => {
    setFormData({
      first_name: '',
      last_name: '',
      email: '',
      project_title: '',
      segment_assignments: [],
      identification_comment: '',
      mapping_category_ids: []
    });
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && handleClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Add New Stakeholder</DialogTitle>
          <p className="text-sm text-slate-500 mt-1">
            Add someone who isn't in the list yet. They'll be reviewed by an admin.
          </p>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          {/* Name Fields */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>First Name *</Label>
              <Input
                value={formData.first_name}
                onChange={(e) => setFormData(prev => ({ ...prev, first_name: e.target.value }))}
                placeholder="First name"
              />
            </div>
            <div className="space-y-2">
              <Label>Last Name *</Label>
              <Input
                value={formData.last_name}
                onChange={(e) => setFormData(prev => ({ ...prev, last_name: e.target.value }))}
                placeholder="Last name"
              />
            </div>
          </div>
          
          {/* Email */}
          <div className="space-y-2">
            <Label>Email (optional)</Label>
            <Input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
              placeholder="email@example.com"
            />
          </div>
          
          {/* Role/Title */}
          <div className="space-y-2">
            <Label>Role / Title (optional)</Label>
            <Input
              value={formData.project_title}
              onChange={(e) => setFormData(prev => ({ ...prev, project_title: e.target.value }))}
              placeholder="e.g., Senior Manager, Director"
            />
          </div>
          
          {/* Segments */}
          <div className="space-y-2">
            <Label>Segments (optional)</Label>
            <p className="text-xs text-slate-500">Select one segment per type</p>
            {segmentTypes?.map(st => {
              const typeSegments = segments?.filter(s => s.segment_type_id === st.id) || [];
              const currentAssignment = formData.segment_assignments.find(sa => sa.segment_type_id === st.id);
              
              return (
                <div key={st.id} className="flex items-center gap-2">
                  <span className="text-sm text-slate-600 w-24 shrink-0">{st.name}:</span>
                  <Select
                    value={currentAssignment?.segment_id || '_none'}
                    onValueChange={(value) => {
                      setFormData(prev => {
                        const filtered = prev.segment_assignments.filter(sa => sa.segment_type_id !== st.id);
                        if (value && value !== '_none') {
                          filtered.push({ segment_type_id: st.id, segment_id: value });
                        }
                        return { ...prev, segment_assignments: filtered };
                      });
                    }}
                  >
                    <SelectTrigger className="flex-1">
                      <SelectValue placeholder={`Select ${st.name}`} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_none">None</SelectItem>
                      {typeSegments.map(seg => (
                        <SelectItem key={seg.id} value={seg.id}>{seg.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              );
            })}
          </div>
          
          {/* Comment about their role */}
          <div className="space-y-2">
            <Label>How do you see their role? (optional)</Label>
            <p className="text-xs text-slate-500">This helps admins understand the context</p>
            <Textarea
              value={formData.identification_comment}
              onChange={(e) => setFormData(prev => ({ ...prev, identification_comment: e.target.value }))}
              placeholder="Describe how you work with this person or their role in the project..."
              rows={3}
            />
          </div>
          
          {/* Mapping Categories - Required */}
          <div className="space-y-2">
            <Label>How do they relate to you? *</Label>
            {mappingCategories.length === 0 ? (
              <p className="text-sm text-slate-500">No mapping categories configured.</p>
            ) : (
              <div className="grid grid-cols-2 gap-2">
                {mappingCategories.map(cat => {
                  const selected = formData.mapping_category_ids.includes(cat.id);
                  return (
                    <label
                      key={cat.id}
                      className={`flex items-center gap-2 p-3 rounded-lg cursor-pointer transition-all border ${
                        selected
                          ? 'bg-[#5AA2AE]/10 border-[#5AA2AE]'
                          : 'bg-white hover:bg-slate-50 border-slate-200'
                      }`}
                    >
                      <Checkbox
                        checked={selected}
                        onCheckedChange={(checked) => {
                          setFormData(prev => ({
                            ...prev,
                            mapping_category_ids: checked 
                              ? [...prev.mapping_category_ids, cat.id]
                              : prev.mapping_category_ids.filter(id => id !== cat.id)
                          }));
                        }}
                      />
                      <span className="text-sm">{cat.name}</span>
                    </label>
                  );
                })}
              </div>
            )}
          </div>
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!formData.first_name || !formData.last_name || formData.mapping_category_ids.length === 0 || saving}
            className="bg-[#5AA2AE] hover:bg-[#5AA2AE]/90"
          >
            {saving ? 'Adding...' : 'Add Stakeholder'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
});

export default function AboutOthersPage() {
  const { projectId } = useParams();
  const { currentProject, segmentTypes, segments } = useProject();
  const { user } = useAuth();
  
  // Core data states
  const [loading, setLoading] = useState(true);
  const [stakeholders, setStakeholders] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [mappingCategories, setMappingCategories] = useState([]);
  const [selections, setSelections] = useState([]);
  const [responses, setResponses] = useState({}); // { selectionId: { questionId: answer } }
  
  // UI states
  const [viewMode, setViewMode] = useState('split'); // 'list', 'galaxy', 'split', 'grid'
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeSelectionId, setActiveSelectionId] = useState(null);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [selectedTarget, setSelectedTarget] = useState(null);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [saving, setSaving] = useState(false);
  
  // Grid view states
  const [gridSortColumn, setGridSortColumn] = useState(null);
  const [gridSortDirection, setGridSortDirection] = useState('asc');
  const [gridFilters, setGridFilters] = useState({});
  const [editingCell, setEditingCell] = useState(null); // { selectionId, questionId }
  const [editingValue, setEditingValue] = useState('');
  
  // Add New Stakeholder dialog state
  const [addNewDialogOpen, setAddNewDialogOpen] = useState(false);
  // Edit Mapping Categories dialog state
  const [editCategoriesDialog, setEditCategoriesDialog] = useState({ open: false, selectionId: null, categoryIds: [] });
  // Recommended stakeholders
  const [recommendations, setRecommendations] = useState([]);
  // Galaxy states
  const [galaxyData, setGalaxyData] = useState({ nodes: [], edges: [] });
  const [nestingLevel1, setNestingLevel1] = useState('none');
  const [nestingLevel2, setNestingLevel2] = useState('none');
  // Track EXPANDED groups (default is collapsed when grouping is active)
  const [expandedGroups, setExpandedGroups] = useState(new Set());
  const graphRef = useRef();

  // Reset expanded groups when nesting level changes
  useEffect(() => {
    setExpandedGroups(new Set());
  }, [nestingLevel1]);

  // Skip popover state
  const [skipPopoverOpen, setSkipPopoverOpen] = useState({});

  // Fetch all data on mount
  useEffect(() => {
    const fetchData = async () => {
      if (!projectId) return;
      
      try {
        const [
          stakeholdersRes,
          questionsRes,
          categoriesRes,
          selectionsRes,
          responsesRes
        ] = await Promise.all([
          stakeholdersAPI.list(projectId),
          questionsAPI.list(projectId, null, 'about_others'),
          projectMappingCategoriesAPI.list(projectId),
          aboutOthersAPI.getSelections(projectId),
          aboutOthersAPI.getResponses(projectId)
        ]);
        
        setStakeholders(stakeholdersRes.data?.stakeholders || stakeholdersRes.data || []);
        setQuestions(questionsRes.data || []);
        setMappingCategories(categoriesRes.data?.categories || []);
        setSelections(selectionsRes.data?.selections || []);
        setResponses(responsesRes.data?.responses || {});
        
        // Set first selection as active if exists
        if (selectionsRes.data?.selections?.length > 0) {
          setActiveSelectionId(selectionsRes.data.selections[0].id);
        }
      } catch (err) {
        console.error('Error fetching data:', err);
        toast.error('Failed to load data');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [projectId]);


  // Fetch recommended stakeholders when add dialog opens
  useEffect(() => {
    if (!addDialogOpen || !projectId) return;
    const fetchRecs = async () => {
      try {
        const res = await aboutOthersAPI.getRecommendations(projectId);
        setRecommendations(res.data?.recommendations || []);
      } catch { setRecommendations([]); }
    };
    fetchRecs();
  }, [addDialogOpen, projectId, selections]);

  // Fetch galaxy data when selections change
  useEffect(() => {
    const fetchGalaxyData = async () => {
      if (!projectId || selections.length === 0) {
        setGalaxyData({ nodes: [], edges: [] });
        return;
      }
      
      try {
        const response = await aboutOthersAPI.getGalaxyData(projectId);
        setGalaxyData({
          nodes: response.data?.nodes || [],
          edges: response.data?.edges || [],
          segments: response.data?.segments || [],
          segment_types: response.data?.segment_types || []
        });
      } catch (err) {
        console.error('Error fetching galaxy data:', err);
      }
    };
    fetchGalaxyData();
  }, [projectId, selections]);

  // Process galaxy data for nesting
  const processedGalaxyData = useCallback(() => {
    if (!galaxyData.nodes.length) return { nodes: [], links: [] };
    
    const segmentTypeMap = {};
    (galaxyData.segment_types || segmentTypes).forEach(st => {
      segmentTypeMap[st.id] = st;
    });
    
    const segmentMap = {};
    (galaxyData.segments || segments).forEach(s => {
      segmentMap[s.id] = s;
    });
    
    // If no nesting, return flat structure
    if (nestingLevel1 === 'none') {
      return {
        nodes: galaxyData.nodes.map(n => ({
          ...n,
          val: n.is_center ? 36 : (n.type === 'stakeholder' ? 24 : 30),
          color: n.is_center ? '#5AA2AE' : n.type === 'stakeholder' ? '#f59e0b' : '#8b5cf6'
        })),
        links: galaxyData.edges.map(e => ({
          source: e.source,
          target: e.target,
          label: e.category_name
        }))
      };
    }
    
    // Handle "mapping" grouping - group by mapping categories
    if (nestingLevel1 === 'mapping') {
      const groups = {};
      
      // Create groups for each mapping category
      mappingCategories.forEach(cat => {
        groups[`mapping_${cat.id}`] = {
          id: `mapping_${cat.id}`,
          name: cat.name,
          categoryId: cat.id,
          nodes: [],
          isGroup: true,
          expanded: expandedGroups.has(`mapping_${cat.id}`)
        };
      });
      
      // Add "Other" group for nodes without mapping categories
      groups['mapping_other'] = {
        id: 'mapping_other',
        name: 'Uncategorized',
        categoryId: null,
        nodes: [],
        isGroup: true,
        expanded: expandedGroups.has('mapping_other')
      };
      
      // Group nodes by their mapping categories
      // The backend returns mapping_category_ids directly on each node
      galaxyData.nodes.forEach(node => {
        if (node.is_center) return;
        
        // Use the mapping_category_ids directly from the node (returned by backend)
        const categoryIds = node.mapping_category_ids || [];
        
        if (categoryIds.length === 0) {
          groups['mapping_other'].nodes.push(node);
        } else {
          // Add node to each category group it belongs to
          categoryIds.forEach(catId => {
            const groupId = `mapping_${catId}`;
            if (groups[groupId]) {
              groups[groupId].nodes.push({ ...node, primaryCategory: catId, allCategoryIds: categoryIds });
            }
          });
        }
      });
      
      // Remove empty groups
      Object.keys(groups).forEach(key => {
        if (groups[key].nodes.length === 0) {
          delete groups[key];
        }
      });
      
      // Build processed nodes and links
      const processedNodes = [];
      const processedLinks = [];
      
      // Add center node
      const centerNode = galaxyData.nodes.find(n => n.is_center);
      if (centerNode) {
        processedNodes.push({
          ...centerNode,
          val: 40,
          color: '#5AA2AE'
        });
      }
      
      // Add group nodes
      Object.values(groups).forEach(group => {
        const groupNodeSize = group.expanded 
          ? 32 + Math.min(group.nodes.length * 2, 16)
          : 38 + Math.min(group.nodes.length * 2, 20);
        
        processedNodes.push({
          id: group.id,
          name: group.name,
          type: 'group',
          isGroup: true,
          isMappingGroup: true,
          expanded: group.expanded,
          nodeCount: group.nodes.length,
          val: groupNodeSize,
          color: group.expanded ? '#0891b2' : '#06b6d4', // Cyan for mapping groups
          childNodes: group.nodes
        });
        
        // Link from center to group
        if (group.nodes.length > 0) {
          processedLinks.push({
            source: centerNode?.id,
            target: group.id,
            label: '',
            curvature: 0,
            isGroupLink: true
          });
        }
        
        if (group.expanded) {
          // Add child nodes
          const uniqueNodeIds = new Set();
          group.nodes.forEach((node, index) => {
            // Avoid duplicate nodes (node might be in multiple categories)
            const nodeId = `${group.id}_${node.id}`;
            if (uniqueNodeIds.has(node.id)) return;
            uniqueNodeIds.add(node.id);
            
            processedNodes.push({
              ...node,
              id: nodeId,
              originalId: node.id,
              val: node.type === 'stakeholder' ? 22 : 26,
              color: node.type === 'stakeholder' ? '#f59e0b' : '#a78bfa',
              groupId: group.id,
              groupName: group.name,
              isChildNode: true,
              childIndex: index,
              allCategoryIds: node.allCategoryIds || []
            });
            
            processedLinks.push({
              source: group.id,
              target: nodeId,
              label: '',
              curvature: 0,
              isChildLink: true
            });
            
            // Add faint cross-group links for nodes in multiple categories
            if (node.allCategoryIds && node.allCategoryIds.length > 1) {
              node.allCategoryIds.forEach(catId => {
                if (catId !== group.categoryId) {
                  const otherGroupId = `mapping_${catId}`;
                  // Only add link if the other group exists
                  if (groups[otherGroupId]) {
                    processedLinks.push({
                      source: nodeId,
                      target: otherGroupId,
                      label: '',
                      curvature: 0.3,
                      isCrossGroupLink: true
                    });
                  }
                }
              });
            }
          });
        }
      });
      
      return { nodes: processedNodes, links: processedLinks };
    }
    
    // Group nodes by segment (based on selected segment type)
    const groups = {};
    const nodeToGroup = {};
    
    galaxyData.nodes.forEach(node => {
      if (node.is_center) return;
      
      // Find which segment this node belongs to (based on the selected segment type)
      const segmentIds = node.segment_ids || [];
      let groupId = 'ungrouped';
      let groupSegment = null;
      
      for (const segId of segmentIds) {
        const segment = segmentMap[segId];
        if (segment && segment.segment_type_id === nestingLevel1) {
          groupId = `group_${segId}`;
          groupSegment = segment;
          break;
        }
      }
      
      if (!groups[groupId]) {
        groups[groupId] = {
          id: groupId,
          name: groupSegment?.name || 'Other',
          segmentId: groupSegment?.id,
          nodes: [],
          isGroup: true,
          // Groups are COLLAPSED by default, expanded only if in expandedGroups set
          expanded: expandedGroups.has(groupId)
        };
      }
      groups[groupId].nodes.push(node);
      nodeToGroup[node.id] = groupId;
    });
    
    // Build processed nodes and links
    const processedNodes = [];
    const processedLinks = [];
    
    // Add center node
    const centerNode = galaxyData.nodes.find(n => n.is_center);
    if (centerNode) {
      processedNodes.push({
        ...centerNode,
        val: 40,
        color: '#5AA2AE'
      });
    }
    
    // Add group nodes - collapsed by default, show children only when expanded
    Object.values(groups).forEach(group => {
      // Check if this group's segment itself is a selection (user selected the segment)
      const segmentSelection = selections.find(s => 
        s.target_type === 'segment' && s.target_id === group.segmentId
      );
      
      // Always add the parent group node (visible in both states)
      const groupNodeSize = group.expanded 
        ? 32 + Math.min(group.nodes.length * 2, 16) // Slightly smaller when expanded
        : 38 + Math.min(group.nodes.length * 2, 20);
      
      processedNodes.push({
        id: group.id,
        name: group.name,
        type: 'group',
        isGroup: true,
        expanded: group.expanded,
        nodeCount: group.nodes.length,
        val: groupNodeSize,
        color: group.expanded ? '#7c3aed' : '#8b5cf6',
        childNodes: group.nodes,
        // If this segment itself is selected, make it clickable for questions
        selection_id: segmentSelection?.id || null,
        target_id: group.segmentId,
        isSelectable: !!segmentSelection
      });
      
      // Link from center to group (always present)
      const hasConnection = galaxyData.edges.some(e => 
        group.nodes.some(n => n.id === e.target)
      );
      if (hasConnection) {
        processedLinks.push({
          source: centerNode?.id,
          target: group.id,
          label: '',
          curvature: 0,
          isGroupLink: true
        });
      }
      
      if (group.expanded) {
        // Expanded state - add child nodes connected to the parent group
        // But skip segment nodes that match this group (avoid duplication)
        group.nodes.forEach((node, index) => {
          // Skip if this node is a segment selection that matches the parent group
          const isGroupSegment = node.type === 'segment' && node.target_id === group.segmentId;
          if (isGroupSegment) {
            return; // Don't duplicate the segment as a child
          }
          
          processedNodes.push({
            ...node,
            val: node.type === 'stakeholder' ? 22 : 26,
            color: node.type === 'stakeholder' ? '#f59e0b' : '#a78bfa',
            groupId: group.id,
            groupName: group.name,
            isChildNode: true,
            childIndex: index
          });
          
          // Link from parent group to child node (radial expansion)
          processedLinks.push({
            source: group.id,
            target: node.id,
            label: '',
            curvature: 0,
            isChildLink: true
          });
        });
      }
    });
    
    return { nodes: processedNodes, links: processedLinks };
  }, [galaxyData, nestingLevel1, expandedGroups, segments, segmentTypes, selections, mappingCategories]);

  // Handle double-click to toggle expand/collapse
  const handleNodeDoubleClick = useCallback((node) => {
    if (node.isGroup) {
      // Toggle expansion for group nodes
      setExpandedGroups(prev => {
        const next = new Set(prev);
        if (next.has(node.id)) {
          next.delete(node.id);
        } else {
          next.add(node.id);
        }
        return next;
      });
      
      // Gently reheat the simulation for smooth transition
      if (graphRef.current) {
        graphRef.current.d3ReheatSimulation();
      }
    } else if (node.groupId) {
      // Collapse the group this node belongs to
      setExpandedGroups(prev => {
        const next = new Set(prev);
        next.delete(node.groupId);
        return next;
      });
      
      if (graphRef.current) {
        graphRef.current.d3ReheatSimulation();
      }
    }
  }, []);

  // Filter stakeholders and segments for search
  const searchResults = useCallback(() => {
    const query = searchQuery.toLowerCase();
    if (!query) return { stakeholders: [], segments: [] };
    
    const filteredStakeholders = stakeholders.filter(s => {
      const fullName = `${s.first_name} ${s.last_name}`.toLowerCase();
      const alreadySelected = selections.some(sel => sel.target_type === 'stakeholder' && sel.target_id === s.id);
      return fullName.includes(query) && !alreadySelected;
    });
    
    const filteredSegments = segments.filter(s => {
      const alreadySelected = selections.some(sel => sel.target_type === 'segment' && sel.target_id === s.id);
      return s.name.toLowerCase().includes(query) && !alreadySelected;
    });
    
    return { stakeholders: filteredStakeholders, segments: filteredSegments };
  }, [searchQuery, stakeholders, segments, selections]);

  // Get stakeholder description (role + segments)
  const getStakeholderDescription = useCallback((stakeholder) => {
    const parts = [];
    
    // Add role/title if known
    if (stakeholder.project_title) {
      parts.push(stakeholder.project_title);
    }
    
    // Add segment names
    if (stakeholder.segment_assignments?.length > 0) {
      const segmentNames = stakeholder.segment_assignments
        .map(sa => {
          const segment = segments.find(s => s.id === sa.segment_id);
          return segment?.name;
        })
        .filter(Boolean);
      
      if (segmentNames.length > 0) {
        parts.push(segmentNames.join(', '));
      }
    }
    
    return parts.length > 0 ? parts.join(' - ') : 'Stakeholder';
  }, [segments]);

  // Get segment type name for a segment
  const getSegmentTypeName = useCallback((segment) => {
    const segmentType = segmentTypes?.find(st => st.id === segment.segment_type_id);
    return segmentType?.name || 'Segment';
  }, [segmentTypes]);

  // Handle adding a new selection
  const handleAddSelection = async () => {
    if (!selectedTarget || selectedCategories.length === 0) {
      toast.error('Please select a stakeholder/segment and at least one relationship type');
      return;
    }
    
    setSaving(true);
    try {
      const response = await aboutOthersAPI.createSelection(projectId, {
        target_type: selectedTarget.type,
        target_id: selectedTarget.id,
        mapping_category_ids: selectedCategories
      });
      
      setSelections(prev => [...prev, response.data]);
      setActiveSelectionId(response.data.id);
      setAddDialogOpen(false);
      setSelectedTarget(null);
      setSelectedCategories([]);
      setSearchQuery('');
      toast.success('Added successfully');
    } catch (err) {
      toast.error('Failed to add selection');
    } finally {
      setSaving(false);
    }
  };

  // Handle removing a selection
  const handleRemoveSelection = async (selectionId) => {
    try {
      await aboutOthersAPI.deleteSelection(projectId, selectionId);
      setSelections(prev => prev.filter(s => s.id !== selectionId));
      if (activeSelectionId === selectionId) {
        setActiveSelectionId(selections.length > 1 ? selections[0].id : null);
      }
      toast.success('Removed successfully');
    } catch (err) {
      toast.error('Failed to remove');
    }
  };

  // Handle editing mapping categories for a selection
  const handleOpenEditCategories = (e, selection) => {
    e.stopPropagation();
    setEditCategoriesDialog({
      open: true,
      selectionId: selection.id,
      categoryIds: [...(selection.mapping_category_ids || [])]
    });
  };

  const handleSaveEditCategories = async () => {
    if (editCategoriesDialog.categoryIds.length === 0) {
      toast.error('Please select at least one relationship type');
      return;
    }
    setSaving(true);
    try {
      await aboutOthersAPI.updateSelection(projectId, editCategoriesDialog.selectionId, {
        mapping_category_ids: editCategoriesDialog.categoryIds
      });
      setSelections(prev => prev.map(s =>
        s.id === editCategoriesDialog.selectionId
          ? { ...s, mapping_category_ids: editCategoriesDialog.categoryIds }
          : s
      ));
      setEditCategoriesDialog({ open: false, selectionId: null, categoryIds: [] });
      toast.success('Relationship types updated');
    } catch (err) {
      toast.error('Failed to update relationship types');
    } finally {
      setSaving(false);
    }
  };

  // Handle adding a new stakeholder (user-identified)
  const handleAddNewStakeholder = async (stakeholderData) => {
    if (!stakeholderData.first_name || !stakeholderData.last_name) {
      toast.error('Please enter at least first and last name');
      return;
    }
    if (stakeholderData.mapping_category_ids.length === 0) {
      toast.error('Please select at least one relationship type');
      return;
    }
    
    // Clean up data - convert empty strings to null for optional fields
    const cleanData = {
      ...stakeholderData,
      email: stakeholderData.email?.trim() || null,
      project_title: stakeholderData.project_title?.trim() || null,
      identification_comment: stakeholderData.identification_comment?.trim() || null,
    };
    
    setSaving(true);
    try {
      const response = await aboutOthersAPI.addNewStakeholder(projectId, cleanData);
      
      // Refresh stakeholders list
      const stakeholdersRes = await stakeholdersAPI.list(projectId);
      setStakeholders(stakeholdersRes.data?.stakeholders || stakeholdersRes.data || []);
      
      // Refresh selections
      const selectionsRes = await aboutOthersAPI.getSelections(projectId);
      setSelections(selectionsRes.data?.selections || []);
      
      setAddNewDialogOpen(false);
      toast.success('New stakeholder added and ready for admin review');
    } catch (err) {
      // Handle Pydantic validation errors which come as array of objects
      const detail = err.response?.data?.detail;
      let errorMsg = 'Failed to add new stakeholder';
      
      if (typeof detail === 'string') {
        errorMsg = detail;
      } else if (Array.isArray(detail) && detail.length > 0) {
        // Pydantic errors: [{type, loc, msg, input, ctx}]
        errorMsg = detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ');
      } else if (detail && typeof detail === 'object') {
        errorMsg = detail.msg || detail.message || JSON.stringify(detail);
      }
      
      toast.error(errorMsg);
    } finally {
      setSaving(false);
    }
  };

  // Handle response updates with auto-save
  const updateResponse = (selectionId, questionId, value) => {
    setResponses(prev => ({
      ...prev,
      [selectionId]: {
        ...(prev[selectionId] || {}),
        [questionId]: value
      }
    }));
    
    // Auto-save with debounce
    debouncedSaveResponse(selectionId, questionId, value);
  };

  // Debounced save to prevent too many API calls
  const saveTimeoutRef = useRef({});
  const [savingIndicator, setSavingIndicator] = useState(false);
  
  const debouncedSaveResponse = useCallback((selectionId, questionId, value) => {
    const key = `${selectionId}-${questionId}`;
    
    // Clear existing timeout for this field
    if (saveTimeoutRef.current[key]) {
      clearTimeout(saveTimeoutRef.current[key]);
    }
    
    // Set new timeout
    saveTimeoutRef.current[key] = setTimeout(() => {
      saveResponse(selectionId, questionId, value);
      delete saveTimeoutRef.current[key];
    }, 500); // 500ms debounce
  }, []);

  const saveResponse = useCallback(async (selectionId, questionId, value) => {
    try {
      setSavingIndicator(true);
      await aboutOthersAPI.saveResponses(projectId, [{
        selection_id: selectionId,
        question_id: questionId,
        answer: value
      }]);
      // Brief delay to show saving indicator
      setTimeout(() => setSavingIndicator(false), 300);
    } catch (err) {
      console.error('Failed to save response:', err);
      setSavingIndicator(false);
      toast.error('Failed to save response');
    }
  }, [projectId]);

  // Skip question handlers
  const handleSkipQuestion = (selectionId, questionId, reason) => {
    updateResponse(selectionId, questionId, { skipped: true, reason });
    setSkipPopoverOpen(prev => ({ ...prev, [`${selectionId}-${questionId}`]: false }));
    toast.success('Question skipped');
  };

  const handleUnskipQuestion = (selectionId, questionId, questionType) => {
    let defaultValue = '';
    if (questionType === 'checkbox') defaultValue = [];
    if (questionType === 'slider' || questionType === 'rating') defaultValue = null; // Reset to unanswered
    updateResponse(selectionId, questionId, defaultValue);
    toast.success('Question restored');
  };

  const isQuestionSkipped = (selectionId, questionId) => {
    const answer = responses[selectionId]?.[questionId];
    return answer && typeof answer === 'object' && answer.skipped === true;
  };

  // Get category names for a selection
  const getCategoryNames = (selection) => {
    return selection.mapping_category_ids
      .map(catId => mappingCategories.find(c => c.id === catId)?.name)
      .filter(Boolean)
      .join(', ');
  };

  // Get active selection data
  const activeSelection = selections.find(s => s.id === activeSelectionId);

  // Calculate progress for a selection
  const getSelectionProgress = (selectionId) => {
    const selResponses = responses[selectionId] || {};
    const answered = Object.values(selResponses).filter(v => {
      if (v && typeof v === 'object' && v.skipped) return true;
      if (Array.isArray(v)) return v.length > 0;
      return v !== undefined && v !== '';
    }).length;
    return { answered, total: questions.length };
  };

  // Render grid cell value display
  const renderGridCellValue = (question, answer, skipped) => {
    if (skipped) {
      return <span className="text-[10px] text-slate-400 italic">Skipped</span>;
    }
    
    if (answer === undefined || answer === '' || (Array.isArray(answer) && answer.length === 0)) {
      return <span className="text-slate-300 text-xs">—</span>;
    }
    
    switch (question.question_type) {
      case 'slider':
        const sliderConfig = question.slider_config || {};
        const sliderMax = sliderConfig.max || 10;
        return (
          <div className="flex items-center gap-1">
            <div className="w-full bg-slate-100 rounded-full h-1.5 max-w-[60px]">
              <div 
                className="bg-[#5AA2AE] h-1.5 rounded-full" 
                style={{ width: `${(answer / sliderMax) * 100}%` }}
              />
            </div>
            <span className="text-xs font-medium text-slate-600">{answer}</span>
          </div>
        );
      case 'rating':
        return (
          <div className="flex items-center gap-0.5">
            {[...Array(question.scale_max || 5)].map((_, i) => (
              <div 
                key={i} 
                className={`w-2 h-2 rounded-full ${i < answer ? 'bg-amber-400' : 'bg-slate-200'}`} 
              />
            ))}
          </div>
        );
      case 'multiple_choice':
      case 'dropdown':
        return <span className="text-xs text-slate-700 truncate">{answer}</span>;
      case 'checkbox':
        return (
          <div className="flex flex-wrap gap-0.5">
            {(answer || []).slice(0, 2).map((opt, i) => (
              <span key={i} className="text-[10px] bg-slate-100 px-1 py-0.5 rounded truncate max-w-[60px]" title={opt}>{opt}</span>
            ))}
            {(answer || []).length > 2 && <span className="text-[10px] text-slate-400">+{answer.length - 2}</span>}
          </div>
        );
      case 'yes_no':
        return (
          <span className={`text-xs font-medium ${answer === 'Yes' ? 'text-green-600' : 'text-red-500'}`}>
            {answer}
          </span>
        );
      case 'free_text':
        return (
          <span className="text-xs text-slate-700 line-clamp-2" title={answer}>
            {answer?.substring(0, 50)}{answer?.length > 50 ? '...' : ''}
          </span>
        );
      default:
        return <span className="text-xs text-slate-700">{String(answer)}</span>;
    }
  };

  // Render grid cell editor - uses popover style for better usability
  const renderGridCellEditor = (question, selectionId, currentValue) => {
    const handleSave = (value) => {
      updateResponse(selectionId, question.id, value);
      setEditingCell(null);
      setEditingValue('');
    };
    
    const handleCancel = () => {
      setEditingCell(null);
      setEditingValue('');
    };
    
    // Common wrapper for popover-style editors
    const EditorWrapper = ({ children, wide = false }) => (
      <div className={`absolute left-0 top-0 z-[100] bg-white rounded-lg shadow-xl border border-slate-200 p-3 ${wide ? 'min-w-[280px]' : 'min-w-[180px]'}`}>
        {children}
      </div>
    );
    
    switch (question.question_type) {
      case 'slider':
        const sliderConfig = question.slider_config || {};
        const sliderMin = sliderConfig.min || sliderConfig.min_value || 0;
        const sliderMax = sliderConfig.max || sliderConfig.max_value || 10;
        // Use 100 steps across the range for fine-grained control
        const sliderStep = (sliderMax - sliderMin) / 100;
        const isSliderUnanswered = editingValue === null || editingValue === undefined || editingValue === '';
        return (
          <EditorWrapper wide={true}>
            <div className="space-y-3">
              <div className="relative">
                {isSliderUnanswered ? (
                  // Empty slider track for unanswered state
                  <div 
                    className="relative flex w-full touch-none select-none items-center h-4 cursor-pointer"
                    onClick={(e) => {
                      const rect = e.currentTarget.getBoundingClientRect();
                      const percent = (e.clientX - rect.left) / rect.width;
                      const rawValue = sliderMin + percent * (sliderMax - sliderMin);
                      const value = Math.round(rawValue / sliderStep) * sliderStep;
                      setEditingValue(parseFloat(Math.max(sliderMin, Math.min(sliderMax, value)).toFixed(2)));
                    }}
                  >
                    <div className="relative h-1.5 w-full grow overflow-hidden rounded-full bg-slate-200 border border-dashed border-slate-300">
                      {/* No filled range - empty track */}
                    </div>
                    <div className="absolute left-1/2 -translate-x-1/2 w-5 h-5 rounded-full border-2 border-dashed border-slate-400 bg-white flex items-center justify-center animate-pulse">
                      <div className="w-1.5 h-1.5 rounded-full bg-slate-400" />
                    </div>
                  </div>
                ) : (
                  <Slider
                    value={[editingValue]}
                    min={sliderMin}
                    max={sliderMax}
                    step={sliderStep}
                    onValueChange={([v]) => setEditingValue(parseFloat(v.toFixed(2)))}
                    className="w-full"
                  />
                )}
              </div>
              <div className="flex justify-end gap-2 pt-1">
                <Button size="sm" variant="outline" className="h-7 px-3" onClick={handleCancel}>
                  Cancel
                </Button>
                <Button 
                  size="sm" 
                  className="h-7 px-3 bg-[#5AA2AE]" 
                  onClick={() => handleSave(isSliderUnanswered ? (sliderMin + sliderMax) / 2 : editingValue)}
                  disabled={isSliderUnanswered}
                >
                  Save
                </Button>
              </div>
            </div>
          </EditorWrapper>
        );
      
      case 'rating':
        return (
          <EditorWrapper>
            <div className="space-y-2">
              <div className="flex items-center justify-center gap-2">
                {[...Array(question.scale_max || 5)].map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setEditingValue(i + 1)}
                    className={`w-6 h-6 rounded-full transition-all ${
                      i < (editingValue || 0) ? 'bg-amber-400 scale-110' : 'bg-slate-200 hover:bg-slate-300'
                    }`}
                  />
                ))}
              </div>
              <div className="flex justify-end gap-2 pt-1">
                <Button size="sm" variant="outline" className="h-7 px-3" onClick={handleCancel}>
                  Cancel
                </Button>
                <Button size="sm" className="h-7 px-3 bg-[#5AA2AE]" onClick={() => handleSave(editingValue)}>
                  Save
                </Button>
              </div>
            </div>
          </EditorWrapper>
        );
      
      case 'multiple_choice':
      case 'dropdown':
        return (
          <EditorWrapper>
            <div className="space-y-2">
              <Select value={editingValue || ''} onValueChange={(v) => { handleSave(v); }}>
                <SelectTrigger className="h-8 text-sm">
                  <SelectValue placeholder="Select..." />
                </SelectTrigger>
                <SelectContent className="z-[150]">
                  {question.options?.map(opt => (
                    <SelectItem key={opt} value={opt} className="text-sm">{opt}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="flex justify-end">
                <Button size="sm" variant="outline" className="h-7 px-3" onClick={handleCancel}>
                  Cancel
                </Button>
              </div>
            </div>
          </EditorWrapper>
        );
      
      case 'checkbox':
        return (
          <EditorWrapper>
            <div className="space-y-2">
              <div className="max-h-[150px] overflow-y-auto space-y-1">
                {question.options?.map(opt => {
                  const selected = (editingValue || []).includes(opt);
                  return (
                    <label key={opt} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-slate-50 p-1 rounded">
                      <Checkbox
                        checked={selected}
                        onCheckedChange={(checked) => {
                          const current = editingValue || [];
                          setEditingValue(checked ? [...current, opt] : current.filter(o => o !== opt));
                        }}
                        className="h-4 w-4"
                      />
                      <span>{opt}</span>
                    </label>
                  );
                })}
              </div>
              <div className="flex justify-end gap-2 pt-1 border-t">
                <Button size="sm" variant="outline" className="h-7 px-3" onClick={handleCancel}>
                  Cancel
                </Button>
                <Button size="sm" className="h-7 px-3 bg-[#5AA2AE]" onClick={() => handleSave(editingValue)}>
                  Save
                </Button>
              </div>
            </div>
          </EditorWrapper>
        );
      
      case 'yes_no':
        return (
          <EditorWrapper>
            <div className="flex gap-2 justify-center">
              <Button 
                size="sm" 
                variant={editingValue === 'Yes' ? 'default' : 'outline'} 
                className={`h-8 px-4 ${editingValue === 'Yes' ? 'bg-green-500 hover:bg-green-600' : ''}`}
                onClick={() => handleSave('Yes')}
              >
                Yes
              </Button>
              <Button 
                size="sm" 
                variant={editingValue === 'No' ? 'default' : 'outline'} 
                className={`h-8 px-4 ${editingValue === 'No' ? 'bg-red-500 hover:bg-red-600' : ''}`}
                onClick={() => handleSave('No')}
              >
                No
              </Button>
              <Button size="sm" variant="ghost" className="h-8 px-2" onClick={handleCancel}>
                <X className="h-4 w-4" />
              </Button>
            </div>
          </EditorWrapper>
        );
      
      case 'free_text':
      default:
        return (
          <EditorWrapper wide={true}>
            <div className="space-y-2">
              <Input
                value={editingValue || ''}
                onChange={(e) => setEditingValue(e.target.value)}
                className="h-8 text-sm"
                placeholder="Enter response..."
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSave(editingValue);
                  }
                  if (e.key === 'Escape') handleCancel();
                }}
              />
              <div className="flex justify-end gap-2">
                <Button size="sm" variant="outline" className="h-7 px-3" onClick={handleCancel}>
                  Cancel
                </Button>
                <Button size="sm" className="h-7 px-3 bg-[#5AA2AE]" onClick={() => handleSave(editingValue)}>
                  Save
                </Button>
              </div>
            </div>
          </EditorWrapper>
        );
    }
  };

  if (loading) {
    return (
      <div className="space-y-6 animate-fade-in">
        <Skeleton className="h-10 w-48" />
        <Skeleton className="h-[600px] w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            About Others
          </h1>
          <p className="text-slate-600 mt-1">
            Identify stakeholders and segments, define relationships, and share your perspective
          </p>
        </div>
        <div className="flex items-center gap-2">
          {/* View mode toggle */}
          <div className="flex items-center bg-slate-100 rounded-lg p-1">
            <Button
              variant={viewMode === 'list' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('list')}
              className={viewMode === 'list' ? 'bg-white shadow-sm text-slate-700' : 'text-slate-500'}
            >
              <List className="h-4 w-4" />
            </Button>
            <Button
              variant={viewMode === 'split' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('split')}
              className={viewMode === 'split' ? 'bg-white shadow-sm text-slate-700' : 'text-slate-500'}
            >
              <LayoutGrid className="h-4 w-4" />
            </Button>
            <Button
              variant={viewMode === 'galaxy' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('galaxy')}
              className={viewMode === 'galaxy' ? 'bg-white shadow-sm text-slate-700' : 'text-slate-500'}
            >
              <GalaxyIcon className="h-4 w-4" />
            </Button>
            <Button
              variant={viewMode === 'grid' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('grid')}
              className={viewMode === 'grid' ? 'bg-white shadow-sm text-slate-700' : 'text-slate-500'}
              title="Grid View"
            >
              <Table2 className="h-4 w-4" />
            </Button>
          </div>
          
          {/* Add Selection Button */}
          <Button 
            onClick={() => setAddDialogOpen(true)} 
            className="bg-[#5AA2AE] hover:bg-[#5AA2AE]/90"
          >
            <Search className="mr-2 h-4 w-4" />
            Search Stakeholders
          </Button>
        </div>
      </div>

      {/* Main Content Area */}
      <div className={`grid gap-6 ${viewMode === 'split' ? 'grid-cols-1 lg:grid-cols-2' : ''}`}>
        
        {/* Selections List */}
        {(viewMode === 'list' || viewMode === 'split') && (
          <Card className="dashboard-card">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <Users className="h-5 w-5 text-[#5AA2AE]" />
                My Stakeholders ({selections.length})
              </CardTitle>
              <CardDescription>
                People and groups you've identified
              </CardDescription>
            </CardHeader>
            <CardContent>
              {selections.length === 0 ? (
                <div className="text-center py-8">
                  <UsersRound className="h-12 w-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500 mb-4">No stakeholders or segments selected yet</p>
                  <Button 
                    onClick={() => setAddDialogOpen(true)}
                    variant="outline"
                  >
                    <Plus className="mr-2 h-4 w-4" />
                    Add Your First Stakeholder
                  </Button>
                </div>
              ) : (
                <div className="space-y-2 max-h-[400px] overflow-y-auto">
                  {selections.map((selection) => {
                    const progress = getSelectionProgress(selection.id);
                    const isActive = activeSelectionId === selection.id;
                    
                    return (
                      <div
                        key={selection.id}
                        onClick={() => setActiveSelectionId(selection.id)}
                        className={`flex items-center gap-2 sm:gap-3 p-2.5 sm:p-3 rounded-lg cursor-pointer transition-all ${
                          isActive 
                            ? 'bg-[#5AA2AE]/10 border-2 border-[#5AA2AE]' 
                            : 'bg-slate-50 hover:bg-slate-100 border-2 border-transparent'
                        }`}
                      >
                        <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center flex-shrink-0 text-xs sm:text-sm ${
                          selection.target_type === 'stakeholder' 
                            ? 'bg-amber-100 text-amber-600' 
                            : 'bg-purple-100 text-purple-600'
                        }`}>
                          {selection.target_type === 'stakeholder' 
                            ? selection.target_name?.split(' ').map(n => n[0]).join('').slice(0, 2)
                            : <Layers className="h-5 w-5" />
                          }
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-slate-900 truncate">{selection.target_name}</p>
                          <p className="text-xs text-slate-500 truncate">
                            {getCategoryNames(selection)}
                          </p>
                        </div>
                        <div className="flex items-center gap-1 flex-shrink-0">
                          <Badge variant="outline" className="text-xs whitespace-nowrap">
                            {progress.answered}/{progress.total}
                          </Badge>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => handleOpenEditCategories(e, selection)}
                            className="h-7 w-7 p-0 text-slate-400 hover:text-[#5AA2AE]"
                            title="Edit relationship types"
                            data-testid={`edit-categories-btn-${selection.id}`}
                          >
                            <Edit className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* My Galaxy Visualization */}
        {(viewMode === 'galaxy' || viewMode === 'split') && (
          <Card className="dashboard-card">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <GalaxyIcon className="h-5 w-5 text-[#5AA2AE]" />
                    My Galaxy
                  </CardTitle>
                  <CardDescription>
                    Your stakeholder network
                  </CardDescription>
                </div>
                {(segmentTypes.length > 0 || mappingCategories.length > 0) && (
                  <div className="flex items-center gap-2">
                    <Select value={nestingLevel1} onValueChange={setNestingLevel1}>
                      <SelectTrigger className="w-36 h-8 text-xs">
                        <SelectValue placeholder="Group by..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">No grouping</SelectItem>
                        {segmentTypes.map(st => (
                          <SelectItem key={st.id} value={st.id}>{st.name}</SelectItem>
                        ))}
                        {mappingCategories.length > 0 && (
                          <SelectItem value="mapping">Mapping Categories</SelectItem>
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {selections.length === 0 ? (
                <div className="text-center py-16">
                  <GalaxyIcon className="h-16 w-16 text-slate-200 mx-auto mb-3" />
                  <p className="text-slate-500">Add stakeholders to see your galaxy</p>
                </div>
              ) : (
                <div className="h-[400px] bg-slate-50 rounded-lg overflow-hidden relative">
                  <ForceGraph2D
                    ref={graphRef}
                    graphData={processedGalaxyData()}
                    width={viewMode === 'split' ? 500 : 950}
                    height={400}
                    // Spacing configuration - smoother transitions
                    d3AlphaDecay={0.03}
                    d3VelocityDecay={0.4}
                    d3AlphaMin={0.001}
                    cooldownTime={2000}
                    warmupTicks={50}
                    // Force configuration for radial expansion
                    d3Force={(name, forceFn) => {
                      if (name === 'link') {
                        // Shorter links for child-parent connections, longer for main connections
                        return forceFn.distance(link => {
                          if (link.isChildLink) return 80; // Child to parent - shorter
                          if (link.isGroupLink) return 160; // Group to center - longer
                          return 140;
                        }).strength(link => {
                          if (link.isChildLink) return 0.8; // Stronger pull for children
                          return 0.4;
                        });
                      }
                      if (name === 'charge') {
                        // Repulsion - child nodes push away from parent center
                        return forceFn.strength(node => {
                          if (node.isChildNode) return -200; // Children repel each other
                          if (node.isGroup && node.expanded) return -400; // Expanded groups have moderate repulsion
                          if (node.isGroup) return -500; // Collapsed groups repel strongly
                          if (node.is_center) return -600; // Center repels strongly
                          return -300;
                        }).distanceMax(400);
                      }
                    }}
                    // Add collision force after graph is ready
                    onEngineTick={() => {
                      if (graphRef.current) {
                        const fg = graphRef.current;
                        // Add collision force if not already added
                        if (!fg._collisionForceAdded) {
                          const collisionForce = d3.forceCollide()
                            .radius(node => {
                              if (node.isChildNode) return (node.val || 20) / 2 + 12;
                              return (node.val || 20) / 2 + 18;
                            })
                            .strength(1)
                            .iterations(2);
                          fg.d3Force('collision', collisionForce);
                          fg._collisionForceAdded = true;
                        }
                      }
                    }}
                    onEngineStop={() => {
                      if (graphRef.current) {
                        graphRef.current.zoomToFit(400, 60);
                      }
                    }}
                    // Node configuration
                    nodeRelSize={6}
                    nodeVal={node => node.val || 20}
                    // Hover labels
                    nodeLabel={node => {
                      if (node.is_center) return 'You';
                      if (node.type === 'stakeholder' || node.isChildNode) return node.name;
                      if (node.isGroup && !node.expanded) return `${node.name} (${node.nodeCount} people) - Double-click to expand`;
                      if (node.isGroup && node.expanded) return `${node.name} - Double-click to collapse`;
                      return '';
                    }}
                    // Link configuration
                    linkColor={link => {
                      if (link.isCrossGroupLink) return 'rgba(14, 165, 233, 0.3)'; // Faint sky blue
                      if (link.isChildLink) return '#c4b5fd';
                      return '#94a3b8';
                    }}
                    linkWidth={link => {
                      if (link.isCrossGroupLink) return 1;
                      if (link.isChildLink) return 1.5;
                      return 2;
                    }}
                    linkDirectionalArrowLength={0}
                    linkCurvature={link => link.curvature || 0}
                    linkLineDash={link => link.isCrossGroupLink ? [4, 4] : null}
                    // Interaction handlers
                    onNodeClick={(node) => {
                      // Handle click for selection
                      if (!node.is_center && node.selection_id) {
                        setActiveSelectionId(node.selection_id);
                      }
                      
                      // Handle double-click detection via timing
                      const now = Date.now();
                      if (node._lastClickTime && now - node._lastClickTime < 500) {
                        // Double-click detected
                        handleNodeDoubleClick(node);
                        node._lastClickTime = 0;
                      } else {
                        node._lastClickTime = now;
                      }
                    }}
                    // Custom node rendering
                    nodeCanvasObjectMode={() => 'replace'}
                    nodeCanvasObject={(node, ctx, globalScale) => {
                      const size = (node.val || 20);
                      const radius = size / 2;
                      
                      // Draw node circle with shadow
                      ctx.shadowColor = 'rgba(0,0,0,0.2)';
                      ctx.shadowBlur = 8;
                      ctx.shadowOffsetX = 2;
                      ctx.shadowOffsetY = 2;
                      ctx.beginPath();
                      ctx.arc(node.x, node.y, radius, 0, 2 * Math.PI, false);
                      ctx.fillStyle = node.color || '#999';
                      ctx.fill();
                      ctx.shadowColor = 'transparent';
                      
                      // Draw border
                      if (node.isGroup && !node.expanded) {
                        // Dashed border for collapsed groups
                        ctx.setLineDash([4 / globalScale, 2 / globalScale]);
                        ctx.strokeStyle = '#4338ca';
                        ctx.lineWidth = 3 / globalScale;
                      } else if (node.isGroup && node.expanded) {
                        // Solid glowing border for expanded groups
                        ctx.setLineDash([]);
                        ctx.strokeStyle = '#a78bfa';
                        ctx.lineWidth = 3 / globalScale;
                        // Draw outer glow ring
                        ctx.beginPath();
                        ctx.arc(node.x, node.y, radius + 4 / globalScale, 0, 2 * Math.PI, false);
                        ctx.strokeStyle = 'rgba(167, 139, 250, 0.3)';
                        ctx.lineWidth = 6 / globalScale;
                        ctx.stroke();
                        // Redraw main circle border
                        ctx.beginPath();
                        ctx.arc(node.x, node.y, radius, 0, 2 * Math.PI, false);
                        ctx.strokeStyle = '#7c3aed';
                        ctx.lineWidth = 2.5 / globalScale;
                      } else if (node.isChildNode) {
                        // Thin border for child nodes
                        ctx.setLineDash([]);
                        ctx.strokeStyle = 'rgba(255,255,255,0.8)';
                        ctx.lineWidth = 1.5 / globalScale;
                      } else {
                        ctx.setLineDash([]);
                        ctx.strokeStyle = 'rgba(255,255,255,0.6)';
                        ctx.lineWidth = 2 / globalScale;
                      }
                      ctx.stroke();
                      ctx.setLineDash([]);
                      
                      // Draw highlight border for selected node
                      if (node.selection_id === activeSelectionId) {
                        ctx.strokeStyle = '#14b8a6';
                        ctx.lineWidth = 4 / globalScale;
                        ctx.stroke();
                      }
                      
                      // Get initials for stakeholders
                      const getInitials = (name) => {
                        if (!name) return '?';
                        const parts = name.split(' ').filter(p => p.length > 0);
                        if (parts.length >= 2) {
                          return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
                        }
                        return name.substring(0, 2).toUpperCase();
                      };
                      
                      // Draw content inside node
                      if (node.is_center) {
                        // Center node - show "You"
                        const fontSize = Math.max(12, radius * 0.55) / globalScale;
                        ctx.font = `bold ${fontSize}px Inter, sans-serif`;
                        ctx.textAlign = 'center';
                        ctx.textBaseline = 'middle';
                        ctx.fillStyle = '#ffffff';
                        ctx.fillText('You', node.x, node.y);
                      } else if (node.isGroup && !node.expanded) {
                        // Collapsed group - show count inside
                        const count = node.nodeCount?.toString() || '0';
                        const fontSize = Math.max(11, radius * 0.45) / globalScale;
                        ctx.font = `bold ${fontSize}px Inter, sans-serif`;
                        ctx.textAlign = 'center';
                        ctx.textBaseline = 'middle';
                        ctx.fillStyle = '#ffffff';
                        ctx.fillText(count, node.x, node.y);
                        
                        // Draw group name BELOW the node
                        const labelFontSize = Math.max(10, 13 / globalScale);
                        ctx.font = `600 ${labelFontSize}px Inter, sans-serif`;
                        ctx.textAlign = 'center';
                        ctx.textBaseline = 'top';
                        ctx.fillStyle = '#334155';
                        
                        let displayName = node.name;
                        if (displayName.length > 20) {
                          displayName = displayName.substring(0, 18) + '...';
                        }
                        ctx.fillText(displayName, node.x, node.y + radius + 8 / globalScale);
                      } else if (node.isGroup && node.expanded) {
                        // Expanded group - show name with collapse indicator
                        const labelFontSize = Math.max(9, radius * 0.35) / globalScale;
                        ctx.font = `bold ${labelFontSize}px Inter, sans-serif`;
                        ctx.textAlign = 'center';
                        ctx.textBaseline = 'middle';
                        ctx.fillStyle = '#ffffff';
                        ctx.fillText('−', node.x, node.y); // Minus sign to indicate collapsible
                        
                        // Draw group name BELOW the node
                        const nameFontSize = Math.max(10, 13 / globalScale);
                        ctx.font = `600 ${nameFontSize}px Inter, sans-serif`;
                        ctx.textAlign = 'center';
                        ctx.textBaseline = 'top';
                        ctx.fillStyle = '#334155';
                        
                        let displayName = node.name;
                        if (displayName.length > 20) {
                          displayName = displayName.substring(0, 18) + '...';
                        }
                        ctx.fillText(displayName, node.x, node.y + radius + 6 / globalScale);
                      } else if (node.isChildNode || node.type === 'stakeholder') {
                        // Child stakeholder node - show initials inside
                        const initials = getInitials(node.name);
                        const fontSize = Math.max(8, radius * 0.5) / globalScale;
                        ctx.font = `bold ${fontSize}px Inter, sans-serif`;
                        ctx.textAlign = 'center';
                        ctx.textBaseline = 'middle';
                        ctx.fillStyle = '#ffffff';
                        ctx.fillText(initials, node.x, node.y);
                      } else if (node.type === 'segment') {
                        // Segment - show name below
                        const labelFontSize = Math.max(10, 12 / globalScale);
                        ctx.font = `600 ${labelFontSize}px Inter, sans-serif`;
                        ctx.textAlign = 'center';
                        ctx.textBaseline = 'top';
                        ctx.fillStyle = '#334155';
                        
                        let displayName = node.name;
                        if (displayName.length > 18) {
                          displayName = displayName.substring(0, 16) + '...';
                        }
                        ctx.fillText(displayName, node.x, node.y + radius + 6 / globalScale);
                      }
                    }}
                  />
                  {nestingLevel1 !== 'none' && (
                    <div className="absolute bottom-2 left-2 text-xs text-slate-500 bg-white/80 px-2 py-1 rounded">
                      Double-click to expand/collapse groups
                    </div>
                  )}
                  {/* Legend */}
                  <div className="absolute top-2 right-2 bg-white/90 backdrop-blur-sm rounded-lg px-3 py-2 shadow-sm">
                    <div className="flex items-center gap-4 text-xs">
                      <div className="flex items-center gap-1.5">
                        <div className="w-3 h-3 rounded-full bg-[#5AA2AE]" />
                        <span className="text-slate-600">You</span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <div className="w-3 h-3 rounded-full bg-amber-500" />
                        <span className="text-slate-600">People</span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <div className="w-3 h-3 rounded-full bg-purple-500" />
                        <span className="text-slate-600">Segments</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>

      {/* Questions Section - Shows when a selection is active */}
      {activeSelection && questions.length > 0 && viewMode !== 'grid' && (
        <Card className="dashboard-card">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Target className="h-5 w-5 text-[#5AA2AE]" />
                  Questions about: {activeSelection.target_name}
                </CardTitle>
                <CardDescription>
                  Share your perspective on this {activeSelection.target_type}
                </CardDescription>
                <PrivacyReminder className="mt-2" />
              </div>
              {savingIndicator && (
                <span className="text-xs text-slate-500 flex items-center gap-1">
                  <span className="w-2 h-2 bg-[#5AA2AE] rounded-full animate-pulse" />
                  Saving...
                </span>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {/* Individual view - always show this */}
            <div className="space-y-6">
              {questions.map((question, qIndex) => {
                const selectionId = activeSelection.id;
                const answer = responses[selectionId]?.[question.id];
                const skipped = isQuestionSkipped(selectionId, question.id);
                const skipReasonText = skipped 
                  ? SKIP_REASONS.find(r => r.value === answer?.reason)?.label || answer?.reason
                  : null;
                  
                  return (
                    <div 
                      key={question.id} 
                      className={`p-4 rounded-lg border ${skipped ? 'bg-slate-50 border-slate-200' : 'bg-white'}`}
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1">
                          {question.heading && (
                            <p className="text-xs font-semibold text-[#5AA2AE] uppercase tracking-wide mb-1">
                              {question.heading}
                            </p>
                          )}
                          <p className={`font-medium ${skipped ? 'text-slate-500' : 'text-slate-900'}`}>
                            {question.text}
                            {question.required && !question.allow_skip && <span className="text-red-500 ml-1">*</span>}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          {question.allow_skip && !skipped && (
                            <Popover
                              open={skipPopoverOpen[`${selectionId}-${question.id}`] || false}
                              onOpenChange={(open) => setSkipPopoverOpen(prev => ({ 
                                ...prev, 
                                [`${selectionId}-${question.id}`]: open 
                              }))}
                            >
                              <PopoverTrigger asChild>
                                <Button variant="ghost" size="sm" className="text-slate-400 hover:text-slate-600 h-7 px-2 text-xs">
                                  <SkipForward className="w-3 h-3 mr-1" />
                                  Skip
                                </Button>
                              </PopoverTrigger>
                              <PopoverContent className="w-56 p-2" align="end">
                                <div className="space-y-1">
                                  <p className="text-sm font-medium text-slate-700 px-2 py-1">Why are you skipping?</p>
                                  {SKIP_REASONS.map((reason) => (
                                    <button
                                      key={reason.value}
                                      onClick={() => handleSkipQuestion(selectionId, question.id, reason.value)}
                                      className="w-full text-left px-2 py-1.5 text-sm rounded hover:bg-slate-100"
                                    >
                                      {reason.label}
                                    </button>
                                  ))}
                                </div>
                              </PopoverContent>
                            </Popover>
                          )}
                          <span className="text-xs text-slate-400">Q{qIndex + 1}</span>
                        </div>
                      </div>
                      
                      {skipped ? (
                        <div className="flex items-center justify-between p-3 bg-slate-100 rounded-lg">
                          <div className="flex items-center gap-2 text-slate-500">
                            <Ban className="w-4 h-4" />
                            <span className="text-sm">Skipped: {skipReasonText}</span>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleUnskipQuestion(selectionId, question.id, question.question_type)}
                            className="text-[#5AA2AE] hover:text-[#5AA2AE]/80 text-xs"
                          >
                            Answer this question
                          </Button>
                        </div>
                      ) : (
                        <>
                          {/* Free Text */}
                          {question.question_type === 'free_text' && (
                            <Textarea
                              placeholder="Type your answer..."
                              value={answer || ''}
                              onChange={(e) => updateResponse(selectionId, question.id, e.target.value)}
                              rows={2}
                            />
                          )}
                          
                          {/* Slider */}
                          {question.question_type === 'slider' && (() => {
                            const config = question.slider_config || {};
                            const minVal = config.min_value ?? config.min ?? 0;
                            const maxVal = config.max_value ?? config.max ?? 10;
                            const stepSize = (maxVal - minVal) / 100; // 100 points
                            const currentVal = answer;
                            const isUnanswered = currentVal === null || currentVal === undefined;
                            
                            return (
                              <div className="space-y-2">
                                {isUnanswered ? (
                                  // Empty slider track for unanswered state
                                  <div 
                                    className="relative flex w-full touch-none select-none items-center h-4 cursor-pointer"
                                    onClick={(e) => {
                                      const rect = e.currentTarget.getBoundingClientRect();
                                      const percent = (e.clientX - rect.left) / rect.width;
                                      const rawValue = minVal + percent * (maxVal - minVal);
                                      const value = Math.round(rawValue / stepSize) * stepSize;
                                      updateResponse(selectionId, question.id, parseFloat(Math.max(minVal, Math.min(maxVal, value)).toFixed(2)));
                                    }}
                                  >
                                    <div className="relative h-1.5 w-full grow overflow-hidden rounded-full bg-slate-200 border border-dashed border-slate-300">
                                      {/* No filled range - empty track */}
                                    </div>
                                    <div className="absolute left-1/2 -translate-x-1/2 w-5 h-5 rounded-full border-2 border-dashed border-slate-400 bg-white flex items-center justify-center animate-pulse">
                                      <div className="w-1.5 h-1.5 rounded-full bg-slate-400" />
                                    </div>
                                  </div>
                                ) : (
                                  <Slider
                                    value={[currentVal]}
                                    onValueChange={([value]) => updateResponse(selectionId, question.id, parseFloat(value.toFixed(2)))}
                                    min={minVal}
                                    max={maxVal}
                                    step={stepSize}
                                  />
                                )}
                                <div className="flex justify-between text-xs text-slate-500">
                                  <span style={config.left_color ? { color: config.left_color } : {}}>
                                    {config.left_label || 'Low'}
                                  </span>
                                  <span className="font-medium" style={config.middle_color ? { color: config.middle_color } : {}}>
                                    {config.middle_label || ''}
                                  </span>
                                  <span style={config.right_color ? { color: config.right_color } : {}}>
                                    {config.right_label || 'High'}
                                  </span>
                                </div>
                              </div>
                            );
                          })()}
                          
                          {/* Rating */}
                          {question.question_type === 'rating' && (() => {
                            const isUnanswered = answer === null || answer === undefined;
                            return (
                              <div className={`flex items-center gap-1 ${isUnanswered ? 'opacity-60' : ''}`}>
                                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((num) => (
                                  <button
                                    key={num}
                                    onClick={() => updateResponse(selectionId, question.id, num)}
                                    className={`w-8 h-8 rounded-full text-sm font-medium transition-all ${
                                      !isUnanswered && answer >= num
                                        ? 'bg-[#5AA2AE] text-white'
                                        : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                                    }`}
                                  >
                                    {num}
                                  </button>
                                ))}
                              </div>
                            );
                          })()}
                          
                          {/* Multiple Choice / Dropdown */}
                          {(question.question_type === 'multiple_choice' || question.question_type === 'dropdown') && (
                            <div className="space-y-2">
                              {question.options?.map((option) => (
                                <label
                                  key={option}
                                  className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-all ${
                                    answer === option
                                      ? 'bg-[#5AA2AE]/10 border border-[#5AA2AE]'
                                      : 'bg-slate-50 hover:bg-slate-100 border border-transparent'
                                  }`}
                                >
                                  <input
                                    type="radio"
                                    name={`q-${selectionId}-${question.id}`}
                                    value={option}
                                    checked={answer === option}
                                    onChange={() => updateResponse(selectionId, question.id, option)}
                                    className="sr-only"
                                  />
                                  <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                                    answer === option ? 'border-[#5AA2AE] bg-[#5AA2AE]' : 'border-slate-300'
                                  }`}>
                                    {answer === option && <Check className="w-2.5 h-2.5 text-white" />}
                                  </div>
                                  <span className="text-sm">{option}</span>
                                </label>
                              ))}
                            </div>
                          )}
                          
                          {/* Yes/No */}
                          {question.question_type === 'yes_no' && (
                            <div className="flex gap-3">
                              {['Yes', 'No'].map((option) => (
                                <Button
                                  key={option}
                                  variant={answer === option ? 'default' : 'outline'}
                                  onClick={() => updateResponse(selectionId, question.id, option)}
                                  className={answer === option ? 'bg-[#5AA2AE] hover:bg-[#5AA2AE]/90' : ''}
                                >
                                  {option}
                                </Button>
                              ))}
                            </div>
                          )}
                          
                          {/* Checkbox */}
                          {question.question_type === 'checkbox' && (
                            <div className="space-y-2">
                              {question.options?.map((option) => {
                                const selected = (answer || []).includes(option);
                                return (
                                  <label
                                    key={option}
                                    className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-all ${
                                      selected
                                        ? 'bg-[#5AA2AE]/10 border border-[#5AA2AE]'
                                        : 'bg-slate-50 hover:bg-slate-100 border border-transparent'
                                    }`}
                                  >
                                    <Checkbox
                                      checked={selected}
                                      onCheckedChange={(checked) => {
                                        const current = answer || [];
                                        const updated = checked
                                          ? [...current, option]
                                          : current.filter(o => o !== option);
                                        updateResponse(selectionId, question.id, updated);
                                      }}
                                    />
                                    <span className="text-sm">{option}</span>
                                  </label>
                                );
                              })}
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
          </CardContent>
        </Card>
      )}

      {/* Grid View - Excel-style layout */}
      {viewMode === 'grid' && (
        <Card className="dashboard-card">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Table2 className="h-5 w-5 text-[#5AA2AE]" />
                  Stakeholder Grid
                </CardTitle>
                <CardDescription>
                  View and edit all responses in a spreadsheet format
                </CardDescription>
              </div>
              <div className="flex items-center gap-2">
                {savingIndicator && (
                  <span className="text-xs text-slate-500 flex items-center gap-1">
                    <span className="w-2 h-2 bg-[#5AA2AE] rounded-full animate-pulse" />
                    Saving...
                  </span>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setGridFilters({})}
                  disabled={Object.keys(gridFilters).length === 0}
                >
                  <X className="h-4 w-4 mr-1" />
                  Clear Filters
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {selections.length === 0 ? (
              <div className="text-center py-12">
                <Table2 className="h-12 w-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500 mb-4">Add stakeholders to see them in the grid</p>
                <Button onClick={() => setAddDialogOpen(true)} variant="outline">
                  <Plus className="mr-2 h-4 w-4" />
                  Add Stakeholder
                </Button>
              </div>
            ) : (
              <div className="overflow-hidden border-t">
                <div className="overflow-x-auto" style={{ maxHeight: '70vh' }}>
                  <table className="w-full border-collapse text-sm">
                    <thead className="sticky top-0 z-20 bg-slate-50">
                      <tr>
                        {/* Frozen columns header */}
                        <th className="sticky left-0 z-30 bg-slate-100 border-b border-r-2 border-slate-300 p-0 min-w-[200px]">
                          <div className="flex items-center justify-between p-2 h-full">
                            <span className="font-semibold text-slate-700">Stakeholder</span>
                            <button 
                              onClick={() => {
                                setGridSortColumn('name');
                                setGridSortDirection(prev => gridSortColumn === 'name' && prev === 'asc' ? 'desc' : 'asc');
                              }}
                              className="p-1 hover:bg-slate-200 rounded"
                            >
                              <ArrowUpDown className="h-3 w-3 text-slate-500" />
                            </button>
                          </div>
                        </th>
                        <th className="sticky left-[200px] z-30 bg-slate-100 border-b border-r border-slate-200 p-0 min-w-[120px]">
                          <div className="flex items-center justify-between p-2 h-full gap-1">
                            <span className="font-semibold text-slate-700 text-xs">Segments</span>
                            <Select 
                              value={gridFilters.segment || 'all'} 
                              onValueChange={(v) => setGridFilters(prev => ({ ...prev, segment: v === 'all' ? undefined : v }))}
                            >
                              <SelectTrigger className="h-5 w-5 p-0 border-0 bg-transparent">
                                <Filter className={`h-3 w-3 ${gridFilters.segment ? 'text-[#5AA2AE]' : 'text-slate-400'}`} />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="all" className="text-xs">All Segments</SelectItem>
                                {segments.map(seg => (
                                  <SelectItem key={seg.id} value={seg.id} className="text-xs">{seg.name}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        </th>
                        <th className="sticky left-[320px] z-30 bg-slate-100 border-b border-r-2 border-slate-300 p-0 min-w-[120px]">
                          <div className="flex items-center justify-between p-2 h-full gap-1">
                            <span className="font-semibold text-slate-700 text-xs">Categories</span>
                            <Select 
                              value={gridFilters.category || 'all'} 
                              onValueChange={(v) => setGridFilters(prev => ({ ...prev, category: v === 'all' ? undefined : v }))}
                            >
                              <SelectTrigger className="h-5 w-5 p-0 border-0 bg-transparent">
                                <Filter className={`h-3 w-3 ${gridFilters.category ? 'text-[#5AA2AE]' : 'text-slate-400'}`} />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="all" className="text-xs">All Categories</SelectItem>
                                {mappingCategories.map(cat => (
                                  <SelectItem key={cat.id} value={cat.id} className="text-xs">{cat.name}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        </th>
                        {/* Question columns */}
                        {questions.filter(q => q.question_context === 'about_others').map((question, idx) => (
                          <th 
                            key={question.id} 
                            className="bg-slate-100 border-b border-r border-slate-200 p-0 min-w-[150px] max-w-[200px]"
                          >
                            <div className="p-2">
                              <div className="flex items-center justify-between mb-1">
                                <span className="text-[10px] text-slate-400">Q{idx + 1}</span>
                                <button 
                                  onClick={() => {
                                    setGridSortColumn(question.id);
                                    setGridSortDirection(prev => gridSortColumn === question.id && prev === 'asc' ? 'desc' : 'asc');
                                  }}
                                  className="p-0.5 hover:bg-slate-200 rounded"
                                >
                                  <ArrowUpDown className="h-3 w-3 text-slate-400" />
                                </button>
                              </div>
                              {question.heading && (
                                <p className="text-[9px] text-[#5AA2AE] uppercase font-medium truncate">{question.heading}</p>
                              )}
                              <p className="text-xs font-medium text-slate-700 line-clamp-2" title={question.text}>
                                {question.text}
                              </p>
                            </div>
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {(() => {
                        // Filter and sort selections
                        let filteredSelections = [...selections];
                        
                        // Filter by segment
                        if (gridFilters.segment) {
                          filteredSelections = filteredSelections.filter(sel => {
                            const stakeholder = stakeholders.find(s => s.id === sel.target_id);
                            const segmentIds = (stakeholder?.segment_assignments || []).map(sa => sa.segment_id);
                            return segmentIds.includes(gridFilters.segment);
                          });
                        }
                        
                        // Filter by category
                        if (gridFilters.category) {
                          filteredSelections = filteredSelections.filter(sel => {
                            return (sel.mapping_category_ids || []).includes(gridFilters.category);
                          });
                        }
                        
                        // Sort
                        if (gridSortColumn === 'name') {
                          filteredSelections.sort((a, b) => {
                            const nameA = a.target_name?.toLowerCase() || '';
                            const nameB = b.target_name?.toLowerCase() || '';
                            return gridSortDirection === 'asc' 
                              ? nameA.localeCompare(nameB) 
                              : nameB.localeCompare(nameA);
                          });
                        } else if (gridSortColumn) {
                          filteredSelections.sort((a, b) => {
                            const ansA = responses[a.id]?.[gridSortColumn];
                            const ansB = responses[b.id]?.[gridSortColumn];
                            const hasA = ansA !== undefined && ansA !== '';
                            const hasB = ansB !== undefined && ansB !== '';
                            if (!hasA && !hasB) return 0;
                            if (!hasA) return gridSortDirection === 'asc' ? 1 : -1;
                            if (!hasB) return gridSortDirection === 'asc' ? -1 : 1;
                            const strA = String(ansA).toLowerCase();
                            const strB = String(ansB).toLowerCase();
                            return gridSortDirection === 'asc' 
                              ? strA.localeCompare(strB) 
                              : strB.localeCompare(strA);
                          });
                        }
                        
                        if (filteredSelections.length === 0) {
                          return (
                            <tr>
                              <td colSpan={3 + questions.filter(q => q.question_context === 'about_others').length} className="text-center py-8 text-slate-500">
                                No stakeholders match the selected filters
                              </td>
                            </tr>
                          );
                        }
                        
                        return filteredSelections.map((selection) => {
                          // Get stakeholder segments
                          const stakeholder = stakeholders.find(s => s.id === selection.target_id);
                          const stakeholderSegments = (stakeholder?.segment_assignments || [])
                            .map(sa => segments.find(s => s.id === sa.segment_id)?.name)
                            .filter(Boolean);
                          
                          // Calculate progress for this selection
                          const aboutOthersQuestions = questions.filter(q => q.question_context === 'about_others');
                          const selResponses = responses[selection.id] || {};
                          const answeredCount = aboutOthersQuestions.filter(q => {
                            const ans = selResponses[q.id];
                            if (ans && typeof ans === 'object' && ans.skipped) return true;
                            if (Array.isArray(ans)) return ans.length > 0;
                            return ans !== undefined && ans !== '';
                          }).length;
                          const totalQuestions = aboutOthersQuestions.length;
                          const isComplete = answeredCount === totalQuestions && totalQuestions > 0;
                          const progressPercent = totalQuestions > 0 ? (answeredCount / totalQuestions) * 100 : 0;
                          
                          return (
                            <tr 
                              key={selection.id} 
                              className={`border-b hover:bg-blue-50/30 h-[52px] ${activeSelectionId === selection.id ? 'bg-[#5AA2AE]/5' : ''}`}
                            >
                              {/* Stakeholder Name - Frozen with Progress */}
                              <td 
                                className="sticky left-0 z-10 bg-white border-r-2 border-slate-300 p-0 h-[52px]"
                                style={{ backgroundColor: activeSelectionId === selection.id ? 'rgb(90 162 174 / 0.05)' : 'white' }}
                              >
                                <div 
                                  className="flex items-center gap-2 p-2 cursor-pointer hover:bg-slate-50 h-full"
                                  onClick={() => setActiveSelectionId(selection.id)}
                                >
                                  {/* Avatar with progress ring */}
                                  <div className="relative flex-shrink-0">
                                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium ${
                                      selection.target_type === 'stakeholder' 
                                        ? 'bg-amber-100 text-amber-700' 
                                        : 'bg-purple-100 text-purple-700'
                                    }`}>
                                      {selection.target_type === 'stakeholder' 
                                        ? selection.target_name?.split(' ').map(n => n[0]).join('').slice(0, 2)
                                        : <Layers className="h-4 w-4" />
                                      }
                                    </div>
                                    {/* Progress ring */}
                                    {totalQuestions > 0 && (
                                      <svg className="absolute -top-0.5 -left-0.5 w-9 h-9 -rotate-90">
                                        <circle
                                          cx="18"
                                          cy="18"
                                          r="16"
                                          fill="none"
                                          stroke={isComplete ? '#10b981' : '#e2e8f0'}
                                          strokeWidth="2"
                                        />
                                        <circle
                                          cx="18"
                                          cy="18"
                                          r="16"
                                          fill="none"
                                          stroke={isComplete ? '#10b981' : '#5AA2AE'}
                                          strokeWidth="2"
                                          strokeDasharray={`${progressPercent} 100`}
                                          strokeLinecap="round"
                                          className="transition-all duration-300"
                                        />
                                      </svg>
                                    )}
                                    {/* Checkmark for complete */}
                                    {isComplete && (
                                      <div className="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 bg-green-500 rounded-full flex items-center justify-center">
                                        <Check className="w-2 h-2 text-white" />
                                      </div>
                                    )}
                                  </div>
                                  <div className="min-w-0 flex-1">
                                    <p className="font-medium text-slate-900 truncate text-sm leading-tight">{selection.target_name}</p>
                                    <p className="text-[10px] text-slate-400 leading-tight">
                                      {answeredCount}/{totalQuestions} answered
                                    </p>
                                  </div>
                                </div>
                              </td>
                              
                              {/* Segments - Frozen */}
                              <td 
                                className="sticky left-[200px] z-10 bg-white border-r border-slate-200 p-2 h-[52px]"
                                style={{ backgroundColor: activeSelectionId === selection.id ? 'rgb(90 162 174 / 0.05)' : 'white' }}
                              >
                                <div 
                                  className="flex gap-1 items-center overflow-hidden"
                                  title={stakeholderSegments.join(', ')}
                                >
                                  {stakeholderSegments.length > 0 ? (
                                    <>
                                      {stakeholderSegments.slice(0, 2).map((seg, i) => (
                                        <span key={i} className="inline-flex px-1.5 py-0.5 text-[10px] bg-slate-100 text-slate-600 rounded whitespace-nowrap">
                                          {seg.length > 8 ? seg.slice(0, 7) + '…' : seg}
                                        </span>
                                      ))}
                                      {stakeholderSegments.length > 2 && (
                                        <span className="text-[10px] text-slate-400 whitespace-nowrap">+{stakeholderSegments.length - 2}</span>
                                      )}
                                    </>
                                  ) : (
                                    <span className="text-[10px] text-slate-400">—</span>
                                  )}
                                </div>
                              </td>
                              
                              {/* Categories - Frozen */}
                              <td 
                                className="sticky left-[320px] z-10 bg-white border-r-2 border-slate-300 p-2 h-[52px]"
                                style={{ backgroundColor: activeSelectionId === selection.id ? 'rgb(90 162 174 / 0.05)' : 'white' }}
                              >
                                {(() => {
                                  const categoryNames = (selection.mapping_category_ids || [])
                                    .map(catId => mappingCategories.find(c => c.id === catId)?.name)
                                    .filter(Boolean);
                                  return (
                                    <div 
                                      className="flex gap-1 items-center overflow-hidden"
                                      title={categoryNames.join(', ')}
                                    >
                                      {categoryNames.length > 0 ? (
                                        <>
                                          {categoryNames.slice(0, 2).map((catName, idx) => (
                                            <span key={idx} className="inline-flex px-1.5 py-0.5 text-[10px] bg-[#5AA2AE]/10 text-[#5AA2AE] rounded whitespace-nowrap">
                                              {catName.length > 8 ? catName.slice(0, 7) + '…' : catName}
                                            </span>
                                          ))}
                                          {categoryNames.length > 2 && (
                                            <span className="text-[10px] text-slate-400 whitespace-nowrap">+{categoryNames.length - 2}</span>
                                          )}
                                        </>
                                      ) : (
                                        <span className="text-[10px] text-slate-400">—</span>
                                      )}
                                    </div>
                                  );
                                })()}
                              </td>
                              
                              {/* Question Cells */}
                              {questions.filter(q => q.question_context === 'about_others').map((question) => {
                                const answer = responses[selection.id]?.[question.id];
                                const isEditing = editingCell?.selectionId === selection.id && editingCell?.questionId === question.id;
                                const skipped = answer && typeof answer === 'object' && answer.skipped;
                                
                                return (
                                  <td 
                                    key={question.id} 
                                    className="border-r border-slate-100 p-0 min-w-[150px] max-w-[200px] h-[52px] relative"
                                  >
                                    {isEditing ? (
                                      <div className="relative">
                                        {renderGridCellEditor(question, selection.id, answer)}
                                      </div>
                                    ) : (
                                      <div 
                                        className="p-2 cursor-pointer hover:bg-blue-50 h-[52px] flex items-center overflow-hidden"
                                        onClick={() => {
                                          setEditingCell({ selectionId: selection.id, questionId: question.id });
                                          setEditingValue(answer || '');
                                        }}
                                      >
                                        {renderGridCellValue(question, answer, skipped)}
                                      </div>
                                    )}
                                  </td>
                                );
                              })}
                            </tr>
                          );
                        });
                      })()}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Add Selection Dialog */}
      <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Add Stakeholder or Segment</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Search */}
            <div className="space-y-2">
              <Label>Search for a stakeholder or segment</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  placeholder="Type to search..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
              
              {/* Search Results */}
              {searchQuery && (
                <div className="max-h-48 overflow-y-auto border rounded-lg divide-y">
                  {searchResults().stakeholders.map(s => (
                    <div
                      key={s.id}
                      onClick={() => setSelectedTarget({ type: 'stakeholder', id: s.id, name: `${s.first_name} ${s.last_name}` })}
                      className={`flex items-center gap-3 p-3 cursor-pointer hover:bg-slate-50 ${
                        selectedTarget?.id === s.id ? 'bg-[#5AA2AE]/10' : ''
                      }`}
                    >
                      <div className="w-8 h-8 rounded-full bg-amber-100 text-amber-600 flex items-center justify-center text-xs font-medium">
                        {s.first_name?.[0]}{s.last_name?.[0]}
                      </div>
                      <div className="flex-1">
                        <p className="font-medium text-sm">{s.first_name} {s.last_name}</p>
                        <p className="text-xs text-slate-500">{getStakeholderDescription(s)}</p>
                      </div>
                      {selectedTarget?.id === s.id && <Check className="h-4 w-4 text-[#5AA2AE]" />}
                    </div>
                  ))}
                  {searchResults().segments.map(s => (
                    <div
                      key={s.id}
                      onClick={() => setSelectedTarget({ type: 'segment', id: s.id, name: s.name })}
                      className={`flex items-center gap-3 p-3 cursor-pointer hover:bg-slate-50 ${
                        selectedTarget?.id === s.id ? 'bg-[#5AA2AE]/10' : ''
                      }`}
                    >
                      <div className="w-8 h-8 rounded-full bg-purple-100 text-purple-600 flex items-center justify-center">
                        <Layers className="h-4 w-4" />
                      </div>
                      <div className="flex-1">
                        <p className="font-medium text-sm">{s.name}</p>
                        <p className="text-xs text-slate-500">{getSegmentTypeName(s)}</p>
                      </div>
                      {selectedTarget?.id === s.id && <Check className="h-4 w-4 text-[#5AA2AE]" />}
                    </div>
                  ))}
                  {searchResults().stakeholders.length === 0 && searchResults().segments.length === 0 && (
                    <div className="p-4 text-center text-slate-500 text-sm">
                      No results found
                    </div>
                  )}
                </div>
              )}
              
              {/* Add New Button */}
              <div className="pt-2 border-t">
                <Button
                  variant="outline"
                  className="w-full justify-start gap-2"
                  onClick={() => {
                    setAddDialogOpen(false);
                    setAddNewDialogOpen(true);
                    setSearchQuery('');
                  }}
                >
                  <UserPlus className="h-4 w-4" />
                  Add New Stakeholder
                </Button>
              </div>

              {/* Recommended Stakeholders */}
              {recommendations.length > 0 && !searchQuery && !selectedTarget && (
                <div className="space-y-2" data-testid="recommended-stakeholders">
                  <Separator />
                  <Label className="flex items-center gap-1.5 text-sm pt-1">
                    <Users className="h-3.5 w-3.5 text-[#5AA2AE]" />
                    Recommended for You
                  </Label>
                  <div className="space-y-1">
                    {recommendations.map(rec => (
                      <div
                        key={rec.id}
                        onClick={() => setSelectedTarget({ type: 'stakeholder', id: rec.id, name: `${rec.first_name} ${rec.last_name}` })}
                        className="flex items-center gap-3 p-2.5 cursor-pointer hover:bg-[#5AA2AE]/5 rounded-lg border border-transparent hover:border-[#5AA2AE]/20 transition-all"
                        data-testid={`recommended-${rec.id}`}
                      >
                        <div className="w-8 h-8 rounded-full bg-amber-100 text-amber-600 flex items-center justify-center text-xs font-medium flex-shrink-0">
                          {rec.first_name?.[0]}{rec.last_name?.[0]}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm text-slate-900 truncate">{rec.first_name} {rec.last_name}</p>
                          {rec.project_title && <p className="text-xs text-slate-500 truncate">{rec.project_title}</p>}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            
            {/* Selected Target */}
            {selectedTarget && (
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="text-xs text-slate-500 mb-1">Selected:</p>
                <p className="font-medium">{selectedTarget.name}</p>
              </div>
            )}
            
            {/* Mapping Categories */}
            {selectedTarget && (
              <div className="space-y-2">
                <Label>How do they relate to you? (Select one or more)</Label>
                {mappingCategories.length === 0 ? (
                  <p className="text-sm text-slate-500">
                    No mapping categories configured. Ask your admin to set up mapping categories.
                  </p>
                ) : (
                  <div className="grid grid-cols-2 gap-2">
                    {mappingCategories.map(cat => {
                      const selected = selectedCategories.includes(cat.id);
                      return (
                        <label
                          key={cat.id}
                          className={`flex items-center gap-2 p-3 rounded-lg cursor-pointer transition-all border ${
                            selected
                              ? 'bg-[#5AA2AE]/10 border-[#5AA2AE]'
                              : 'bg-white hover:bg-slate-50 border-slate-200'
                          }`}
                        >
                          <Checkbox
                            checked={selected}
                            onCheckedChange={(checked) => {
                              setSelectedCategories(prev => 
                                checked 
                                  ? [...prev, cat.id]
                                  : prev.filter(id => id !== cat.id)
                              );
                            }}
                          />
                          <span className="text-sm">{cat.name}</span>
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setAddDialogOpen(false);
              setSelectedTarget(null);
              setSelectedCategories([]);
              setSearchQuery('');
            }}>
              Cancel
            </Button>
            <Button
              onClick={handleAddSelection}
              disabled={!selectedTarget || selectedCategories.length === 0 || saving}
              className="bg-[#5AA2AE] hover:bg-[#5AA2AE]/90"
            >
              {saving ? 'Adding...' : 'Add Selection'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add New Stakeholder Dialog - Memoized to prevent Galaxy re-renders */}
      <AddNewStakeholderDialog
        open={addNewDialogOpen}
        onClose={() => setAddNewDialogOpen(false)}
        segmentTypes={segmentTypes}
        segments={segments}
        mappingCategories={mappingCategories}
        onSubmit={handleAddNewStakeholder}
        saving={saving}
      />

      {/* Edit Mapping Categories Dialog */}
      <Dialog open={editCategoriesDialog.open} onOpenChange={(open) => !open && setEditCategoriesDialog({ open: false, selectionId: null, categoryIds: [] })}>
        <DialogContent className="max-w-sm" data-testid="edit-categories-dialog">
          <DialogHeader>
            <DialogTitle>Edit Relationship Types</DialogTitle>
            <p className="text-sm text-slate-500 mt-1">
              Select the relationship types for this stakeholder
            </p>
          </DialogHeader>
          <div className="space-y-2 py-2">
            {mappingCategories.map(cat => {
              const checked = editCategoriesDialog.categoryIds.includes(cat.id);
              return (
                <label
                  key={cat.id}
                  className={`flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all ${
                    checked ? 'border-[#5AA2AE] bg-[#5AA2AE]/5' : 'border-slate-100 hover:border-slate-200'
                  }`}
                  data-testid={`edit-category-option-${cat.id}`}
                >
                  <Checkbox
                    checked={checked}
                    onCheckedChange={(val) => {
                      setEditCategoriesDialog(prev => ({
                        ...prev,
                        categoryIds: val
                          ? [...prev.categoryIds, cat.id]
                          : prev.categoryIds.filter(id => id !== cat.id)
                      }));
                    }}
                  />
                  <div>
                    <p className="text-sm font-medium text-slate-900">{cat.name}</p>
                    {cat.description && <p className="text-xs text-slate-500">{cat.description}</p>}
                  </div>
                </label>
              );
            })}
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button
              variant="ghost"
              onClick={(e) => {
                e.stopPropagation();
                handleRemoveSelection(editCategoriesDialog.selectionId);
                setEditCategoriesDialog({ open: false, selectionId: null, categoryIds: [] });
              }}
              className="text-red-500 hover:text-red-700 hover:bg-red-50 mr-auto"
              data-testid="remove-selection-btn"
            >
              <Trash2 className="h-4 w-4 mr-1" />
              Remove
            </Button>
            <Button variant="outline" onClick={() => setEditCategoriesDialog({ open: false, selectionId: null, categoryIds: [] })}>
              Cancel
            </Button>
            <Button
              onClick={handleSaveEditCategories}
              disabled={editCategoriesDialog.categoryIds.length === 0 || saving}
              className="bg-[#5AA2AE] hover:bg-[#5AA2AE]/90"
              data-testid="save-edit-categories-btn"
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
